/*
  The sets table (§6.2–§6.5, §7.2), rebuilt on the task-6 primitives.

  It used to hold its own reducer, its own MUI Paper/Table/Skeleton and its own
  slice-by-page. All three now come from components/DataTable, so what is left here is the
  one thing that is genuinely about sets: the column descriptors.

  Two things a reader should not have to reconstruct:

  - **The columns are built by the VIEW, not by this component.** GameView needs the same
    descriptor list this table renders, because selectTableRows filters and sorts through
    it and the card grid shows those same filtered rows. Building them in two places would
    let the grid and the table disagree about what "matches the filter" means, so
    gameSetsColumns() is exported and GameView owns the one instance.

  - **There is no Category column (D24, §12.33).** GameView maps every set with
    `setCategory: null` and GameSetSummaryDto (types/v2/catalog.ts) has no category field,
    so the column could never hold data. It is dropped rather than sorted.
*/

import { Link } from "react-router-dom";

import {
  DataTable,
  DataTableColumn,
  DataTableEmpty,
  DataTablePagination,
  TableController,
  TableRowsResult,
} from "components/DataTable";
import { LINK } from "components/styles";
import { sanitizeURIPathSegment } from "helpers/routing";

import { Set } from "types/gameInfo";

/*
  gameCode is a parameter rather than a prop read inside a render callback so the list can
  be memoised on it — a fresh array every render would re-run selectTableRows every render.
*/
export const gameSetsColumns = (gameCode: string): DataTableColumn<Set>[] => [
  {
    id: "cardsGraded",
    label: "Total Grades",
    /* A count: right-aligned and formatCount'd by the primitive, and missing sorts as 0. */
    numeric: true,
    sortValue: (row) => row.cardsGraded ?? 0,
    className: "w-32",
  },
  {
    id: "setName",
    label: "Set Name",
    /*
      Sorted, filtered and shown by the display name (pop-language-split D10), so the search
      matches what the visitor reads; the link keeps the raw name the route resolves.
    */
    sortValue: (row) => row.displayName ?? row.setName,
    /* A custom render defaults filterText to "", and the set name is the whole point. */
    filterText: (row) => row.displayName ?? row.setName,
    render: (row) => (
      <Link to={`/${gameCode}/${sanitizeURIPathSegment(row.setName)}`} className={LINK}>
        {row.displayName ?? row.setName}
      </Link>
    ),
  },
  {
    id: "releaseDate",
    label: "Release Date",
    /*
      string | null. sortRows partitions the nulls out and appends them in BOTH directions,
      which is what §6.4 asks for and what the old set-list comparator could not do — it
      returned 0 for a null/string pair, a comparator whose result depended on input order.
    */
    sortValue: (row) => row.releaseDate,
    className: "w-40",
  },
];

export interface GameSetsTableProps {
  /* The same instance the view passed to selectTableRows. */
  columns: readonly DataTableColumn<Set>[];
  /*
    The whole result of selectTableRows, not just its rows: pagination has to be given
    view.page (clamped) and view.filteredCount (post-filter), and taking the result object
    is what makes handing it state.page instead impossible. §6.5's blank-page-after-
    filtering bug lives in that one substitution.
  */
  view: TableRowsResult<Set>;
  table: TableController;
  isLoading: boolean;
  /* RTK Query's error, straight through. The old table had no way to be told (§12.20). */
  error?: unknown;
}

const GameSetsTable = (props: GameSetsTableProps) => {
  const { columns, view, table, isLoading, error } = props;

  return (
    <div className="flex flex-col">
      <DataTable
        columns={columns}
        rows={view.rows}
        getRowKey={(row) => row.id}
        caption="Sets"
        sort={table.state.sort}
        onSortChange={table.setSort}
        isLoading={isLoading}
        error={error}
        emptyState={
          <DataTableEmpty
            title="No sets found"
            description="No loaded set matches this search. Load more sets to search the rest of this game."
          />
        }
        /* Page-sized, so the skeleton is the shape the real page will be. */
        skeletonRows={table.state.rowsPerPage}
      />
      <DataTablePagination
        page={view.page}
        rowsPerPage={table.state.rowsPerPage}
        count={view.filteredCount}
        onChangePage={table.setPage}
        onChangeRowsPerPage={table.setRowsPerPage}
        label="Sets pagination"
      />
    </div>
  );
};

export default GameSetsTable;
